import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

type RecipientType = "all" | "group" | "player";

export default function AdminBroadcast() {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [recipientType, setRecipientType] = useState<RecipientType>("all");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const { data: groups = [] } = useQuery({
    queryKey: ["admin-broadcast-groups", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_groups")
        .select("id, name")
        .eq("organization_id", organization!.id)
        .order("name");
      return data ?? [];
    },
    enabled: !!organization,
  });

  const { data: players = [] } = useQuery({
    queryKey: ["admin-broadcast-players", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("organization_members")
        .select("user_id")
        .eq("organization_id", organization!.id);
      const userIds = (data ?? []).map(m => m.user_id);
      if (userIds.length === 0) return [];
      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name")
        .in("id", userIds);
      return userIds.map(uid => ({
        user_id: uid,
        profiles: (profiles ?? []).find(p => p.id === uid) ?? null,
      }));
    },
    enabled: !!organization && recipientType === "player",
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      let recipientIds: string[] = [];

      if (recipientType === "all") {
        const { data } = await supabase
          .from("organization_members")
          .select("user_id")
          .eq("organization_id", organization!.id);
        recipientIds = (data ?? []).map(m => m.user_id);
      } else if (recipientType === "group") {
        const { data } = await supabase
          .from("group_members")
          .select("player_id, parent_id")
          .eq("group_id", selectedGroupId)
          .eq("status", "active");
        const ids = new Set<string>();
        (data ?? []).forEach(m => {
          ids.add(m.player_id);
          if (m.parent_id) ids.add(m.parent_id);
        });
        recipientIds = Array.from(ids);
      } else {
        recipientIds = [selectedPlayerId];
      }

      // Filter out current user
      recipientIds = recipientIds.filter(id => id !== user!.id);

      if (recipientIds.length === 0) {
        throw new Error(t("broadcast.noRecipientsFound"));
      }

      const rows = recipientIds.map(uid => ({
        user_id: uid,
        organization_id: organization!.id,  // PR-A: org-scope per Phase 1 RLS
        type: "admin_broadcast",
        title,
        body,
        data: { broadcast: true } as any,
      }));

      const { error } = await supabase.from("notifications").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("admin:toast.messageSent"));
      setTitle("");
      setBody("");
    },
    onError: (e: Error) => toast.error(e.message || t("toast.couldNotSend")),
  });

  const canSend =
    title.trim() &&
    body.trim() &&
    (recipientType === "all" ||
      (recipientType === "group" && selectedGroupId) ||
      (recipientType === "player" && selectedPlayerId));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" /> {t("broadcast.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>{t("broadcast.recipients")}</Label>
          <Select value={recipientType} onValueChange={(v) => setRecipientType(v as RecipientType)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("broadcast.allInClub")}</SelectItem>
              <SelectItem value="group">{t("broadcast.allInGroup")}</SelectItem>
              <SelectItem value="player">{t("broadcast.singlePerson")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {recipientType === "group" && (
          <div className="space-y-2">
            <Label>{t("broadcast.selectGroup")}</Label>
            <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
              <SelectTrigger><SelectValue placeholder={t("broadcast.selectGroupPlaceholder")} /></SelectTrigger>
              <SelectContent>
                {groups.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {recipientType === "player" && (
          <div className="space-y-2">
            <Label>{t("broadcast.selectPerson")}</Label>
            <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
              <SelectTrigger><SelectValue placeholder={t("broadcast.selectPersonPlaceholder")} /></SelectTrigger>
              <SelectContent>
                {players.map(p => (
                  <SelectItem key={p.user_id} value={p.user_id}>
                    {p.profiles?.full_name ?? p.user_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label>{t("broadcast.titleLabel")}</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={t("broadcast.titlePlaceholder")} />
        </div>

        <div className="space-y-2">
          <Label>{t("broadcast.messageLabel")}</Label>
          <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder={t("broadcast.messagePlaceholder")} rows={4} />
        </div>

        <Button onClick={() => sendMutation.mutate()} disabled={!canSend || sendMutation.isPending} className="w-full gap-2">
          {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {t("broadcast.send")}
        </Button>
      </CardContent>
    </Card>
  );
}
